/* Splash Comics theme script. Plain web components, no framework, no jQuery. */
(() => {
  'use strict';
  const S = (window.Splash = window.Splash || {});
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------- money ---------- */
  S.money = (cents) => {
    const fmt = S.moneyFormat || '£{{amount}}';
    const n = (cents || 0) / 100;
    const two = n.toFixed(2);
    const groups = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) => {
      const [i, d] = two.split('.');
      if (k === 'amount_no_decimals') return groups(String(Math.round(n)));
      if (k === 'amount_with_comma_separator') return i.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + d;
      return groups(i) + '.' + d;
    });
  };

  /* ---------- toast ---------- */
  let toastTimer;
  S.toast = (msg) => {
    const t = $('#toast');
    if (!t) return;
    t.textContent = msg;
    t.classList.add('on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => t.classList.remove('on'), 2600);
  };

  /* ---------- basket ---------- */
  // The drawer always, plus the basket page section when we're on /cart, so both redraw in place
  const sectionIds = () => {
    const ids = ['cart-drawer'];
    const page = $('[data-cart-section]');
    if (page) ids.push(page.dataset.cartSection);
    return ids;
  };
  const SECTIONS = { join: (sep) => sectionIds().join(sep) };
  S.cart = {
    async add(body) {
      const isForm = body instanceof FormData;
      if (isForm) body.append('sections', SECTIONS.join(','));
      else body.sections = SECTIONS.join(',');
      const res = await fetch(S.routes.cartAdd + '.js', {
        method: 'POST',
        headers: isForm ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: isForm ? body : JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'That did not go in the basket.');
      await S.cart.refresh(data.sections);
      return data;
    },
    // Lines are addressed by position: Shopify re-keys a line when a discount lands on it, so keys go stale
    async change(line, quantity) {
      const which = typeof line === 'number' ? { line } : { id: line };
      const res = await fetch(S.routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...which, quantity, sections: SECTIONS.join(',') }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not change that.');
      await S.cart.refresh(data.sections, data);
      return data;
    },
    async refresh(sections, cart) {
      if (sections && sections['cart-drawer']) {
        const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const fresh = $('[data-cart-drawer-body]', doc);
        const cur = $('[data-cart-drawer-body]');
        if (fresh && cur) cur.replaceWith(fresh);
        $$('[data-tiers]', doc).forEach(S.tiers.update);
      }
      const page = $('[data-cart-section]');
      if (sections && page && sections[page.dataset.cartSection]) {
        const doc = new DOMParser().parseFromString(sections[page.dataset.cartSection], 'text/html');
        const fresh = $('[data-cart-page-body]', doc);
        const cur = $('[data-cart-page-body]');
        if (fresh && cur) cur.replaceWith(fresh);
        $$('[data-tiers]', doc).forEach(S.tiers.update);
      }
      S.tiers.check();
      if (!cart) cart = await fetch(S.routes.cart + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count || '';
        el.dataset.count = cart.item_count;
      });
      $$('[data-cart-total]').forEach((el) => (el.textContent = S.money(cart.total_price)));
      document.dispatchEvent(new CustomEvent('splash:cart', { detail: cart }));
      return cart;
    },
    // Redraw the drawer and basket page from Shopify, e.g. after a refused change, so inputs show the real quantity
    async redraw() {
      const ids = sectionIds().join(',');
      const sections = await fetch(`${location.pathname}?sections=${encodeURIComponent(ids)}`).then((r) => r.json()).catch(() => null);
      return S.cart.refresh(sections);
    },
    after() {
      if (S.cartType === 'page') return (location.href = S.routes.cart);
      if (S.cartType === 'drawer') {
        const d = $('#cart-drawer');
        if (d && !d.open) d.showModal();
      }
      S.toast(S.toastAdded || 'Added to your basket');
    },
  };

  /* ---------- buy more save more tracker ---------- */
  // Display only. The tier comes from the server rendered snippet, this just swaps it in place
  // (so the aria-live region stays put), slides the bar, and thumps the stamp down once per tier rise.
  const TIER_KEY = 'splash:tier';
  const seenTier = () => {
    try { return Number(localStorage.getItem(TIER_KEY)) || 0; } catch (e) { return 0; }
  };
  const saveTier = (n) => {
    try { localStorage.setItem(TIER_KEY, String(n)); } catch (e) {}
  };
  S.tiers = {
    update(fresh) {
      const cur = $(`[data-tiers="${fresh.dataset.tiers}"]`);
      if (!cur) return;
      cur.dataset.tier = fresh.dataset.tier;
      cur.dataset.count = fresh.dataset.count;
      // Same words and bar (say 5 comics down to 4): leave it, so screen readers aren't told twice
      if (cur.innerHTML === fresh.innerHTML && cur.hidden === fresh.hidden) return;
      const to = fresh.style.getPropertyValue('--fill');
      cur.hidden = fresh.hidden;
      cur.innerHTML = fresh.innerHTML; // the bar keeps the old --fill for a frame, then slides
      if (reduced) return cur.style.setProperty('--fill', to);
      void cur.offsetWidth;
      requestAnimationFrame(() => cur.style.setProperty('--fill', to));
    },
    check() {
      const all = $$('[data-tiers]');
      if (!all.length) return;
      const tier = Number(all[0].dataset.tier) || 0;
      const seen = seenTier();
      if (tier < seen) return saveTier(tier);
      if (tier === seen) return;
      // Went up: only play it where someone can see it, otherwise wait for the drawer to open
      const shown = all.find((t) => !t.hidden && t.getClientRects().length);
      if (!shown) return;
      const stamp = $('[data-tier-stamp]', shown);
      if (stamp && !reduced) {
        stamp.classList.remove('is-new');
        void stamp.offsetWidth;
        stamp.classList.add('is-new');
      }
      saveTier(tier);
    },
  };
  document.addEventListener('DOMContentLoaded', () => {
    S.tiers.check();
    const d = $('#cart-drawer');
    if (d) new MutationObserver(() => d.open && S.tiers.check()).observe(d, { attributes: true, attributeFilter: ['open'] });
  });

  /* ---------- checkout upsell: "wait a sec" once, when Check out is pressed ---------- */
  // Asked once per visit. Skipped if the offer is already in the basket. Yes adds it, no carries on,
  // and both go straight to checkout. Closing it (X, Esc or the backdrop) leaves them on the basket.
  const UPSELL_KEY = 'splash:upsell-asked';
  const toCheckout = () => (location.href = S.routes.checkout || '/checkout');
  document.addEventListener('submit', async (e) => {
    const sub = e.submitter;
    const dlg = $('#upsell');
    if (!dlg || !sub || sub.name !== 'checkout') return;
    let asked = false;
    try { asked = sessionStorage.getItem(UPSELL_KEY) === '1'; } catch (ex) {}
    if (asked) return;
    e.preventDefault();
    try {
      const cart = await fetch(S.routes.cart + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const offer = (dlg.dataset.variants || dlg.dataset.variant).split(',').filter(Boolean);
      if (cart.items.some((i) => offer.includes(String(i.variant_id)))) return toCheckout();
    } catch (ex) {
      return toCheckout();
    }
    try { sessionStorage.setItem(UPSELL_KEY, '1'); } catch (ex) {}
    dlg.showModal();
  });
  document.addEventListener('click', async (e) => {
    const dlg = $('#upsell');
    if (!dlg) return;
    if (e.target === dlg) return dlg.close(); // backdrop
    if (e.target.closest('[data-upsell-skip]')) return toCheckout();
    const add = e.target.closest('[data-upsell-add]');
    if (!add) return;
    $$('[data-upsell-add]', dlg).forEach((b) => (b.disabled = true));
    const err = $('[data-upsell-err]', dlg);
    try {
      const res = await fetch(S.routes.cartAdd + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(add.dataset.variant || dlg.dataset.variant), quantity: 1, ...(add.dataset.plan ? { selling_plan: Number(add.dataset.plan) } : {}), ...(dlg.dataset.ships ? { properties: { Ships: dlg.dataset.ships } } : {}) }],
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || 'That one is out for now.');
      toCheckout();
    } catch (ex) {
      $$('[data-upsell-add]', dlg).forEach((b) => (b.disabled = false));
      if (err) {
        err.textContent = ex.message + ' You can still check out without it.';
        err.hidden = false;
      }
    }
  });

  /* ---------- drawers (menu and basket) ---------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open]');
    if (opener) {
      const d = document.getElementById(opener.dataset.open);
      if (d && d.showModal) {
        e.preventDefault();
        d.showModal();
      }
      return;
    }
    const closer = e.target.closest('[data-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }
    if (e.target.matches('dialog.drawer')) e.target.close();
  });

  /* ---------- product form: adds to the basket without leaving the page ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.submit(e));
    }
    async submit(e) {
      e.preventDefault();
      const btn = $('[type=submit]', this.form);
      const err = $('[data-form-error]', this);
      if (err) err.hidden = true;
      btn?.classList.add('is-busy');
      const data = new FormData(this.form);
      // Starter packs are Shopify bundles, and bundles can't go in the basket on a pre-order plan,
      // so drop the plan and the pre-order app's line notes it adds to every form
      if (this.hasAttribute('data-bundle')) {
        for (const k of [...data.keys()]) if (k === 'selling_plan' || /pre-?order/i.test(k)) data.delete(k);
      }
      try {
        await S.cart.add(data);
        S.cart.after();
      } catch (ex) {
        if (err) {
          err.textContent = ex.message;
          err.hidden = false;
        }
      } finally {
        btn?.classList.remove('is-busy');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- variant picker: option chips choose the variant ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = $('script[type="application/json"]', this);
      if (!json) return;
      this.variants = JSON.parse(json.textContent);
      this.formId = this.dataset.form;
      this.addEventListener('change', () => this.update());
      this.update(true);
    }
    update(first) {
      const picked = $$('fieldset', this).map((fs) => $('input:checked', fs)?.value);
      const v = this.variants.find((x) => x.options.every((o, i) => o === picked[i]));
      // cross through values that are sold out given the other choices
      $$('fieldset', this).forEach((fs, idx) => {
        $$('input', fs).forEach((inp) => {
          const test = picked.slice();
          test[idx] = inp.value;
          const match = this.variants.find((x) => x.options.every((o, i) => o === test[i]));
          const out = !match || !match.available;
          inp.closest('.chip')?.classList.toggle('is-out', out);
          inp.disabled = out && this.dataset.disableSold === 'true';
          const sr = $('.sr', inp.closest('.chip'));
          if (sr) sr.textContent = out ? ', sold out' : '';
        });
      });
      const root = this.closest('[data-product-root]') || document;
      const idInput = $(`#${this.formId} [name=id]`) || $('[name=id]', root);
      const btns = $$(`[form="${this.formId}"][type=submit], #${this.formId} [type=submit]`);
      if (!v) {
        btns.forEach((b) => {
          b.disabled = true;
          b.textContent = b.dataset.unavailable || 'Pick a size';
        });
        return;
      }
      if (idInput) idInput.value = v.id;
      btns.forEach((b) => {
        b.disabled = !v.available;
        b.textContent = v.available ? b.dataset.label : b.dataset.soldout || 'Sold out';
      });
      $$('[data-price]', root).forEach((p) => {
        p.innerHTML = S.money(v.price) + (v.compare_at_price > v.price ? `<s>${S.money(v.compare_at_price)}</s>` : '');
      });
      if (!first && this.dataset.url) {
        history.replaceState(null, '', `${this.dataset.url}?variant=${v.id}`);
      }
      this.dispatchEvent(new CustomEvent('splash:variant', { bubbles: true, detail: v }));
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- quantity stepper ---------- */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-step]');
    if (!b) return;
    const input = $('input', b.closest('.qty'));
    if (!input) return;
    const min = Number(input.min || 0);
    input.value = Math.max(min, Number(input.value || 0) + Number(b.dataset.step));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- basket lines: change quantity or remove, in the drawer or the page ---------- */
  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-line-qty]');
    if (!input) return;
    const box = input.closest('[data-cart-lines]');
    box?.classList.add('is-busy');
    try {
      await S.cart.change(input.dataset.line ? Number(input.dataset.line) : input.dataset.key, Number(input.value));
    } catch (ex) {
      S.toast(ex.message);
      S.cart.redraw();
    }
    box?.classList.remove('is-busy');
  });
  document.addEventListener('click', async (e) => {
    const rm = e.target.closest('[data-line-remove]');
    if (!rm) return;
    e.preventDefault();
    try {
      await S.cart.change(rm.dataset.line ? Number(rm.dataset.line) : rm.dataset.key, 0);
    } catch (ex) {
      S.toast(ex.message);
      S.cart.redraw();
    }
  });
  // basket note saves itself
  let noteTimer;
  document.addEventListener('input', (e) => {
    if (!e.target.matches('[data-cart-note]')) return;
    clearTimeout(noteTimer);
    const note = e.target.value;
    noteTimer = setTimeout(() => {
      fetch(S.routes.cartUpdate + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      });
    }, 500);
  });

  /* ---------- the flat lay: plays once in view, tap to replay ---------- */
  class FlatLay extends HTMLElement {
    connectedCallback() {
      if (reduced) return;
      this.classList.add('wait');
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((x) => x.isIntersecting)) {
            io.disconnect();
            this.play();
          }
        },
        { threshold: 0.25 }
      );
      io.observe(this);
      this.addEventListener('click', () => this.play());
    }
    play() {
      this.classList.remove('wait', 'play');
      void this.offsetWidth;
      this.classList.add('play');
    }
  }
  customElements.define('flat-lay', FlatLay);

  /* ---------- sticky dock on phone, once past the first section ---------- */
  class StickyDock extends HTMLElement {
    connectedCallback() {
      document.body.classList.add('has-dock');
      const first = $('main > .shopify-section') || $('main');
      const io = new IntersectionObserver(([e]) => {
        const past = !e.isIntersecting && e.boundingClientRect.top < 0;
        this.firstElementChild.classList.toggle('on', past);
      });
      io.observe(first);
    }
  }
  customElements.define('sticky-dock', StickyDock);

  /* ---------- countdown: "Closes in 4 days 6 hours" ---------- */
  S.timeLeft = (ms) => {
    if (ms <= 0) return '';
    const s = Math.floor(ms / 1000);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const p = (n, w) => `<b>${n}</b> ${w}${n === 1 ? '' : 's'}`;
    if (d > 0) return `${p(d, 'day')} ${p(h, 'hour')}`;
    if (h > 0) return `${p(h, 'hour')} ${p(m, 'min')}`;
    return `${p(m, 'min')} ${p(sec, 'sec')}`;
  };
  class CountDown extends HTMLElement {
    connectedCallback() {
      this.to = Date.parse(this.dataset.to);
      if (!this.to) return (this.style.display = 'none');
      this.out = $('[data-left]', this) || this;
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
    tick() {
      const left = this.to - Date.now();
      if (left <= 0) {
        clearInterval(this.timer);
        this.style.display = 'none';
        this.closest('drop-state')?.decide();
        return;
      }
      this.out.innerHTML = S.timeLeft(left);
    }
  }
  customElements.define('count-down', CountDown);

  /* ---------- drop state: compared in the browser so cached pages stay right ----------
     soon   before early access (or before opening if there's no early access)
     early  early access is open, public launch still to come
     live   open to everyone, until it closes
     closed it has closed (the copy says "back soon" when drop.returning is true) */
  S.dropState = (el) => {
    const now = Date.now();
    const early = Date.parse(el.dataset.early) || 0;
    const opens = Date.parse(el.dataset.opens) || 0;
    const closes = Date.parse(el.dataset.closes) || 0;
    if (closes && now >= closes) return 'closed';
    if (opens && now >= opens) return 'live';
    if (early && now >= early) return 'early';
    if (opens) return 'soon';
    return 'live';
  };
  class DropState extends HTMLElement {
    connectedCallback() {
      this.decide();
    }
    decide() {
      const state = S.dropState(this);
      this.dataset.state = state;
      $$('[data-show]', this.closest('[data-product-root]') || this).forEach((el) => {
        el.hidden = !el.dataset.show.split(' ').includes(state);
      });
    }
  }
  customElements.define('drop-state', DropState);

  /* hides early access products from grids until they're public */
  class DropGate extends HTMLElement {
    connectedCallback() {
      const st = S.dropState(this);
      this.style.display = st === 'early' ? 'none' : 'contents';
    }
  }
  customElements.define('drop-gate', DropGate);

  /* ---------- register interest form: size and answer go into customer tags ---------- */
  class InterestForm extends HTMLElement {
    connectedCallback() {
      this.tags = $('[name="contact[tags]"]', this);
      this.base = this.dataset.base || '';
      // after signing up, bring the thank you message into view
      const done = $('[data-posted]', this);
      if (done) requestAnimationFrame(() => done.scrollIntoView({ block: 'center' }));
      this.addEventListener('change', () => this.build());
      this.build();
    }
    build() {
      if (!this.tags) return;
      const bits = this.base.split(',').map((x) => x.trim()).filter(Boolean);
      $$('input[data-tag]:checked', this).forEach((i) => bits.push(i.dataset.tag));
      if (this.dataset.utm === 'true') {
        // campaign links like ?utm_source=tiktok&utm_campaign=launch end up as customer tags
        const q = new URLSearchParams(location.search);
        const clean = (v) => v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
        if (q.get('utm_source')) bits.push('source-' + clean(q.get('utm_source')));
        if (q.get('utm_campaign')) bits.push('campaign-' + clean(q.get('utm_campaign')));
      }
      this.tags.value = bits.join(', ');
    }
  }
  customElements.define('interest-form', InterestForm);

  /* ---------- gallery: swipe on phone, dots to jump ---------- */
  class GallerySwipe extends HTMLElement {
    connectedCallback() {
      this.track = $('.gal-track', this);
      this.dots = $$('.gal-dots button', this);
      if (!this.track || this.dots.length < 2) return;
      this.dots.forEach((d, i) =>
        d.addEventListener('click', () => this.track.children[i].scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'nearest', inline: 'center' }))
      );
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting) {
              const i = Array.from(this.track.children).indexOf(en.target);
              this.dots.forEach((d, j) => d.setAttribute('aria-current', i === j));
            }
          });
        },
        { root: this.track, threshold: 0.6 }
      );
      Array.from(this.track.children).forEach((c) => io.observe(c));
    }
  }
  customElements.define('gallery-swipe', GallerySwipe);

  /* cover thumbnails on comic pages */
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-thumb]');
    if (!t) return;
    const main = $('[data-main-cover] img');
    if (!main) return;
    main.src = t.dataset.thumb;
    main.removeAttribute('srcset');
    $$('[data-thumb]').forEach((x) => x.setAttribute('aria-current', x === t));
  });

  /* ---------- product recommendations ---------- */
  class ProductRecs extends HTMLElement {
    connectedCallback() {
      const io = new IntersectionObserver(async ([e]) => {
        if (!e.isIntersecting) return;
        io.disconnect();
        try {
          const html = await fetch(this.dataset.url).then((r) => r.text());
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = $('product-recs', doc);
          if (fresh && fresh.querySelector('.card')) this.innerHTML = fresh.innerHTML;
          else this.closest('.shopify-section')?.setAttribute('hidden', '');
        } catch (_) {}
      }, { rootMargin: '400px' });
      io.observe(this);
    }
  }
  customElements.define('product-recs', ProductRecs);

  /* ---------- collection filters: submit as soon as something changes ---------- */
  class FacetForm extends HTMLElement {
    connectedCallback() {
      const form = $('form', this);
      if (!form) return;
      let t;
      form.addEventListener('change', (e) => {
        if (e.target.type === 'number') return;
        form.requestSubmit();
      });
      form.addEventListener('input', (e) => {
        if (e.target.type !== 'number') return;
        clearTimeout(t);
        t = setTimeout(() => form.requestSubmit(), 900);
      });
      form.addEventListener('submit', () => {
        // keep empty fields out of the URL
        $$('input,select', form).forEach((i) => {
          if (i.value === '') i.disabled = true;
        });
      });
    }
  }
  customElements.define('facet-form', FacetForm);

  /* ---------- The Monthly Splash quiz ----------
     Three steps, then straight into the basket with the monthly selling plan, then checkout.
     Answers become line item properties on the subscription line. */
  class MonthlyQuiz extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      this.steps = $$('[data-step-panel]', this);
      this.label = $('[data-step-label]', this);
      this.bars = $$('.prog i', this);
      this.at = 0;
      this.classList.add('js');
      this.show(0, true);
      this.addEventListener('click', (e) => {
        const go = e.target.closest('[data-go]');
        if (go) {
          e.preventDefault();
          this.show(Number(go.dataset.go));
        }
        const chip = e.target.closest('[data-add-char]');
        if (chip) {
          e.preventDefault();
          this.toggleChar(chip);
        }
      });
      const gift = $('[data-gift-toggle]', this);
      gift?.addEventListener('change', () => {
        $('[data-gift-fields]', this).hidden = !gift.checked;
        this.compose();
      });
      this.form.addEventListener('input', () => this.compose());
      this.form.addEventListener('change', () => this.compose());
      this.form.addEventListener('submit', (e) => this.submit(e));
      this.compose();
    }
    show(i, first) {
      this.at = i;
      this.steps.forEach((s, j) => (s.hidden = j !== i));
      this.bars.forEach((b, j) => b.classList.toggle('on', j <= i));
      if (this.label) this.label.textContent = `Step ${i + 1} of ${this.steps.length}`;
      if (!first) {
        const h = $('[data-quiz-title]', this) || this;
        h.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
        h.focus?.({ preventScroll: true });
      }
      this.compose();
    }
    toggleChar(chip) {
      const input = $('[data-chars]', this);
      const name = chip.dataset.addChar;
      let list = input.value.split(',').map((x) => x.trim()).filter(Boolean);
      const has = list.some((x) => x.toLowerCase() === name.toLowerCase());
      list = has ? list.filter((x) => x.toLowerCase() !== name.toLowerCase()) : list.concat(name);
      input.value = list.join(', ');
      chip.setAttribute('aria-pressed', !has);
      this.compose();
    }
    compose() {
      // gather answers into the hidden properties[...] inputs, joined with commas
      const groups = {};
      $$('[data-answer]', this).forEach((el) => {
        const k = el.dataset.answer;
        groups[k] = groups[k] || [];
        if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
        const v = (el.value || '').trim();
        if (v) groups[k].push(v);
      });
      Object.entries(groups).forEach(([k, vals]) => {
        const out = $(`[data-prop="${k}"]`, this);
        if (!out) return;
        out.value = vals.join(', ');
        out.disabled = !out.value;
      });
      const gift = $('[data-gift-toggle]', this);
      $$('[data-gift-prop]', this).forEach((el) => (el.disabled = !gift?.checked || !el.value));
      // summary on the last step
      $$('[data-sum]', this).forEach((dd) => {
        const k = dd.dataset.sum;
        const v = (groups[k] || []).join(', ');
        dd.textContent = v || dd.dataset.empty || 'Anything';
      });
    }
    async submit(e) {
      e.preventDefault();
      if (this.at < this.steps.length - 1) return this.show(this.at + 1);
      const btn = $('[type=submit]', this.steps[this.at]);
      const err = $('[data-form-error]', this);
      err.hidden = true;
      btn?.classList.add('is-busy');
      this.compose();
      try {
        await S.cart.add(new FormData(this.form));
        location.href = this.dataset.next || '/checkout';
      } catch (ex) {
        err.textContent = ex.message;
        err.hidden = false;
        btn?.classList.remove('is-busy');
      }
    }
  }
  customElements.define('monthly-quiz', MonthlyQuiz);
})();
